import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { LoadingState } from '../../../redux/reducers/getLoadingReducer/getLoadingReducer';
import MenuState from '../../../types/menu/MenuState';
import { defaultMatcher, getFormErrorByName, getFormErrorMessageByError, getFormErrorsByName, hasError, MatchingStrategy, useFormErrorContext } from '../../../utils/context/FormErrorContext'
import { useReadOnly } from '../../../utils/ReadOnlyContext'
import './NumberInput.css'
import { selectGetLoading, selectMenu } from '../../../redux/selectors/exportSelectorPaths';
import { getFormWarningByName, getFormWarningMessageByWarning, getFormWarningsByName, hasWarning, useFormWarningContext } from '../../../utils/context/FormWarningContext';

export interface NumberInputProps {
    name?: string;
    className?: string;
    ariaLabel?: string;
    initialValue?: string | number | null;
    decimals?: boolean;
    negatives?: boolean;
    commas?: boolean
    decimalPlaces?: number;
    min?: number;
    max?: number;
    textAlign?: "left" | "right" | "center";
    maxLength?: number;
    onChange?: ((value: number | null) => void) | ((value: number | null, lastValue: number | null) => void);
    symbol?: string;
    tooltip?: string;
    symbolReversed?: boolean;
    border?: boolean;
    errorMatcher?: MatchingStrategy;
    errorBorder?: "inner" | "outer";
    placeholder?: string;
    readOnly?: boolean;
    size?: number;
    // Removed to allow current base rent to update without overwriting user input
    // overrideUserModified?: boolean;
    ariaLabelledBy?: string;
}

function NumberInput(props: NumberInputProps) {
    const { i18n } = useTranslation();
    const {name, className, initialValue, decimals, negatives, commas, decimalPlaces, min, max, textAlign, maxLength, onChange, symbol, symbolReversed, errorMatcher, border, errorBorder, tooltip, placeholder} = props
    const fixedDecimalScale = true
    const loadingState: LoadingState = useSelector(selectGetLoading);
    const menuState: MenuState  = useSelector(selectMenu);
    const [decimalSeparator, setDecimalSeparator] = useState(".")
    const [groupSeparator, setGroupSeparator] = useState(",")
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [minusSign, setMinusSign] = useState("-")

    useEffect(() => {
        const numberWithGroupAndDecimalSeparator = -10000.11;
        const parts = Intl.NumberFormat("en").formatToParts(numberWithGroupAndDecimalSeparator)
        const decimalSeparator = parts.find(part => part.type === "decimal")?.value;
        const groupSeparator = parts.find(part => part.type === "group")?.value;
        const minusSign = parts.find(part => part.type === "minusSign")?.value;
        if (decimalSeparator) {
            setDecimalSeparator(decimalSeparator)
        }
        if (groupSeparator) {
            if (groupSeparator.charCodeAt(0) === 8239) {
                setGroupSeparator(" ")
            } else {
                setGroupSeparator(groupSeparator)
            }
        }
        if (minusSign) {
            setMinusSign(minusSign)
        }
    }, [i18n.language])

    const handleCommaSeparation = useCallback((value: string): string => {
        if (!commas) {
            return value
        }
    
        const isNegative = value.includes("-");
        if (isNegative) {
            value = value.replace("-", "")
        }

        const hasTrailingDecimal = value.endsWith(".")
    
        const [numPart, decPart] = value.split(".")
    
        const commaSeparatedNum = numPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    
        let formatted = commaSeparatedNum

        if(decPart !== undefined && decPart !== "") {
            formatted += "." + decPart
        } else if(hasTrailingDecimal) {
            formatted += "."
        }
        
        return isNegative ? `-${formatted}` : formatted;
    }, [commas]);

    const allowedNumbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]
    const allowedInputChars = [".", "-", ...allowedNumbers]
    const [userModified, setUserModified] = useState(false)
    const [value, setValue] = useState<string>(() => initialValue === undefined || initialValue === null ? "" : handleCommaSeparation(String(initialValue)))
    const [lastNum, setLastNum] = useState<number | null>(() => {
        const num = parseFloat(String(initialValue))
        return isNaN(num) ? null : num
    })
    const { isReadOnly } = useReadOnly()
    const { errors, removeError } = useFormErrorContext()
    const { warnings, removeWarning } = useFormWarningContext();
    const matcher = errorMatcher ? errorMatcher : defaultMatcher
    const errorPrimaryKey = getFormErrorByName(errors, name as string, matcher)?.primaryKey as string
    const warningPrimaryKey = getFormWarningByName(warnings, name as string, matcher)?.primaryKey as string
    useEffect(() => {
        // We don't want the value to be overwritten if the user has already updated it
        // Potentially look for a better solution in the future, but this should work for now
        if(userModified) {
            return
        }

        if(initialValue === undefined || initialValue === null) {
            setValue("")
            setLastNum(null)
            return
        }

        let result = handleCommaSeparation(String(initialValue))
        if (fixedDecimalScale && decimalPlaces !== undefined) {
            const num = parseFloat(String(initialValue))
            if (!isNaN(num)) {
                result = handleCommaSeparation(num.toFixed(decimalPlaces))
            }
        }
        const num = parseFloat(String(initialValue))
        const resultNum = isNaN(num) ? null : num
        setValue(result);
        setLastNum(resultNum)
    }, [initialValue, handleCommaSeparation, userModified, fixedDecimalScale, decimalPlaces]);


    const generateContainerClass = () => {
        let base = "number-input-container"

        if(border && !hasError(errors, name as string, matcher)) {
            base += " number-input-border"
        } else if(errorBorder === "outer" && hasError(errors, name as string, matcher)) {
            base += " number-input-border-error"
        }

        return base
    }

    const generateInputClass = () => {
        let base = "number-input"

        if(errorBorder === "inner" && hasError(errors, name as string, matcher)) {
            base += " number-input-error"
        }

        if(errorBorder === "inner" && hasWarning(warnings, name as string, matcher)) {
            base += " number-input-warning"
        }

        if(className) {
            base += " " + className
        }

        return base
    }

    const filterUnwantedChars = (value: string): string => {
        return [...value].filter(char => allowedInputChars.includes(char)).join('')
    }

    const truncateByMaxLength = (value: string): string => {
        if(maxLength === undefined) {
            return value
        }
        let resultMaxLength = maxLength
        if(!value.endsWith(".") && value.includes(".")) {
            resultMaxLength++
        }
        if(value.includes("-")) {
            resultMaxLength++
        }
        return value.slice(0, resultMaxLength)
    }

    const isDecimalAllowed = (): boolean => {
        if(decimals === false || decimalPlaces === 0) {
            return false;
        }

        return decimals === true || (decimalPlaces !== undefined && decimalPlaces > 0)
    }

    const handleDecimals = (value: string, oldValue: string): string => {
        const currentDecimalAmt = (value.match(/\./g) || []).length
        const prevDecimalAmt = (oldValue.match(/\./g) || []).length
        const allowed = isDecimalAllowed()

        if(currentDecimalAmt > 1 && prevDecimalAmt === 1) {
            return oldValue
        }

        if(!allowed) {
            if(currentDecimalAmt > 1) {
                return oldValue
            }
            if(currentDecimalAmt === 1 && prevDecimalAmt === 0) {
                return oldValue
            }
        }

        return value
    }

    const handleNegative = (value: string): string => {
        if(!negatives) {
            return value.replaceAll("-", "")
        }

        const updated = value.replaceAll("-", "")
        return value.startsWith("-") ? "-" + updated : updated
    }

    const truncateByMaxDecimalPlaces = (value: string): string => {
        if(!isDecimalAllowed()) {
            return value
        }

        const [numPart, decPart] = value.split(".")

        if(decPart === undefined || decPart === '') {
            return value
        }

        return decimalPlaces !== undefined ? numPart + "." + decPart.slice(0, decimalPlaces) : value
    }

    const handleExtraZeros = (value: string): string => {
        if(value === "" || value === "-") {
            return value
        }

        const isNegative = value.startsWith("-")
        const unsigned = isNegative ? value.slice(1) : value
      
        if (!unsigned.includes(".")) {
            const cleaned = unsigned.replace(/^0+/, '') || "0"
            return (isNegative ? "-" : "") + cleaned
        }
      
        const [numPart, decPart] = unsigned.split(".")
        const cleanedInt = numPart.replace(/^0+/, '') || "0"
      
        if (decPart !== undefined && decPart !== "") {
            return (isNegative ? "-" : "") + cleanedInt + "." + decPart
        }
      
        if (unsigned.endsWith(".")) {
            return (isNegative ? "-" : "") + cleanedInt + "."
        }
      
        return (isNegative ? "-" : "") + cleanedInt
    } 

    // Note that this only works properly if you use '0' as min range or similar. If you use '5' as the minRange you wouldn't be able to type '15' since '1' isnt a valid value
    const isInRange = (value: string): boolean => {
        if(value === undefined || value === "" || value === "-" || value === "." || value === "-.") {
            return true
        }

        const num = parseFloat(value)

        if(isNaN(num)) {
            return false
        }

        if(min !== undefined && num < min) {
            return false
        }

        if(max !== undefined && num > max) {
            return false
        }

        return true
    }

   const clampWithinBounds = (old: string, value: string): string => {
        const wasNegative = old.includes("-")
        const isPositive = !value.includes("-")
        const result = parseFloat(value)

        if(isNaN(result)) {
            return value
        }

        if(wasNegative && isPositive && max !== undefined && result > max) {
            return String(max)
        }

        if(!wasNegative && !isPositive && min !== undefined && result < min) {
            return String(min)
        }

        return value
    }

    const preserveCursorPos = (element: HTMLInputElement | null, raw: string, formatted: string) => {
        if(!element || element.selectionStart === null) {
            return
        }

        const cursorIdx = element.selectionStart

  
        const digitsBefore = raw.slice(0, cursorIdx).replace(/[^0-9]/g, '').length
        let digitCount = 0
        let resultCursor = 0

        for(let i = 0; i < formatted.length; i++) {
            if(/\d/.test(formatted[i])) {
                digitCount++
            }

            if(digitCount === digitsBefore) {
                resultCursor = i + 1
                break
            }
        }

        if(digitCount < digitsBefore) {
            resultCursor = formatted.length
        }


        const wasTypingDecimal = raw[cursorIdx - 1] === "."

        if(wasTypingDecimal && formatted[resultCursor] === ".") {
            resultCursor++
        }

        if(raw === "." || raw === "-.") {
            resultCursor = formatted.length
        }

        requestAnimationFrame(() => {
            element.setSelectionRange(resultCursor, resultCursor)
        })
    }

    const onRawChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if(!userModified) {
            setUserModified(true)
        }

        removeError(errorPrimaryKey)
        removeWarning(warningPrimaryKey)
        const element = e.target
        const rawValue = element.value
        const isShorter = rawValue.length < value.length
        
        let result = filterUnwantedChars(rawValue)
        result = handleDecimals(result, value)

        result = handleNegative(result)

        result = handleExtraZeros(result)

        if(!isShorter) {
            result = truncateByMaxLength(result)
            result = truncateByMaxDecimalPlaces(result)
        }

        result = clampWithinBounds(value, result)

        const isValid = isShorter || isInRange(result)
        // Parse number before adding commas to the input
        let num: number | null = parseFloat(result)
        result = handleCommaSeparation(result)

        preserveCursorPos(element, rawValue, result)

        if(isValid) {
            setValue(result)

            if(isNaN(num)) {
                num = null
            }

            if(onChange) {
                onChange(num, lastNum)
            }
            setLastNum(num)
        }
    }

    const clearRedundantChars = (value: string): string => {
        if(value === "." || value === "-") {
            return ""
        }

        if(value.endsWith(".")) {
            value = value.replace(".", "")
        }
        
        const num = parseFloat(value)

        if(num === 0) {
            return value.replace("-", "")
        }

        return value
    }


    const onRawBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        let result = clearRedundantChars(e.target.value)
        if (fixedDecimalScale && decimalPlaces !== undefined && result !== "") {
            const num = parseFloat(result.replaceAll(",", ""))
            if (!isNaN(num)) {
                result = handleCommaSeparation(num.toFixed(decimalPlaces))
            }
        }
        setValue(result)
    }

    const [wrapperClassName, setWrapperClassName] = useState("number-input-wrapper")
    useEffect(() => {
        const wrapperClassName = "number-input-wrapper"
        setWrapperClassName(wrapperClassName)
    }, [decimalSeparator, groupSeparator, loadingState.loading, menuState.activeItem, name, value])

    const isInputReadOnly = props.readOnly !== undefined ? props.readOnly : isReadOnly

    return (
        <div className={generateContainerClass()}>
            <div className={wrapperClassName} style={symbolReversed ? {flexDirection: 'row-reverse'} : {}}>
                {symbol && <div className='number-input-symbol'>{symbol}</div>}
                <input 
                    name={name}
                    type='text'
                    value={(initialValue === undefined || initialValue === null || initialValue === "") ? "" : value}
                    id={name}
                    aria-label={props.ariaLabel ?? name}
                    title={tooltip}
                    onChange={onRawChange}
                    onBlur={onRawBlur}
                    readOnly={isInputReadOnly}
                    disabled={isInputReadOnly}
                    autoComplete="off"
                    style={{"textAlign": textAlign === undefined ? "left" : textAlign}} 
                    data-primary-key={errorPrimaryKey}
                    className={generateInputClass()}
                    placeholder={placeholder}
                    size={props.size}
                    data-testid={name + '-number-input'}
                />
            </div>
            {!isInputReadOnly && getFormErrorsByName(errors, name as string, matcher).map((error, i) => {
                return <p key={i} className="form-error-message">{getFormErrorMessageByError(error)}</p>
            })}
            {!isInputReadOnly && getFormWarningsByName(warnings, name as string, matcher).map((warning, i) => {
                return <p key={i} className="form-warning-message">{getFormWarningMessageByWarning(warning)}</p>
            })}
        </div>
    )
}

export default NumberInput
